import React from 'react';
import ReactDOM from 'react-dom';
import App from './app/App';
import ThemeModeProvider from './app/ThemeModeProvider';
import { registerServiceWorker } from './services/serviceWorkerRegistration';

// ── Native PWA polish ─────────────────────────────────────────────────────
import './styles/native-pwa.css';
import PWAOverlay from './components/pwa/PWAOverlay';
import { initInstallPrompt, isStandalone } from './services/pwa/installPrompt';
import { initSWUpdate } from './services/pwa/swUpdate';
import { silenceResizeObserverErrors } from './utils/silenceResizeObserver';

// Suppress the benign "ResizeObserver loop" warning that otherwise triggers
// the CRA dev error overlay. Runs before React mounts so its listeners are
// registered ahead of the overlay's own.
silenceResizeObserverErrors();

// Tag <html> when running as an installed PWA so CSS can target it
if (isStandalone() && typeof document !== 'undefined') {
  document.documentElement.classList.add('is-standalone');
}
initInstallPrompt();
initSWUpdate();

// Fade out the inline launch splash once React is ready to paint
function hideLaunchSplash() {
  const el = document.getElementById('nexuma-splash');
  if (!el) return;
  // Give the first React frame a chance to paint, then dissolve
  requestAnimationFrame(() => {
    setTimeout(() => {
      el.classList.add('hide');
      setTimeout(() => { el && el.parentNode && el.parentNode.removeChild(el); }, 320);
    }, 60);
  });
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 32,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          background: '#F7F8FA',
          color: '#101828',
          textAlign: 'center',
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
            fontSize: 28,
          }}>⚠️</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
            Diçka shkoi keq
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#5C6B7A', marginBottom: 24, maxWidth: 400 }}>
            Aplikacioni hasi një gabim të papritur. Ju lutemi rifreskoni faqen.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#3E8FA3',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Rifresko faqen
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{
              marginTop: 24,
              padding: 16,
              background: '#FEF2F2',
              borderRadius: 8,
              fontSize: '0.72rem',
              color: '#DC2626',
              maxWidth: 600,
              overflow: 'auto',
              textAlign: 'left',
              border: '1px solid #FECACA',
            }}>
              {this.state.error.toString()}
              {'\n'}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeModeProvider>
        <App />
        <PWAOverlay />
      </ThemeModeProvider>
    </ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root')
);

hideLaunchSplash();
registerServiceWorker();
